import React, { useState, useEffect, useRef } from 'react';
import { Zap, Activity, WifiOff, Info } from 'lucide-react';
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fmt } from '../../utils/safeDate';
import { isStale, ageLabel } from '../../utils/staleness';
import dataService from '../../services/dataService';
import DateRangePicker, { getAggregation, getAggregationMs } from '../common/DateRangePicker';
import AggregationSelector from '../common/AggregationSelector';

const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#3b82f6'];

const readChannels = (latest) => {
  if (!latest) return [];
  return Object.entries(latest)
    .filter(([k]) => /^ch\d{2}_amps$/.test(k))
    .map(([k, amps]) => ({ channel: parseInt(k.slice(2, 4), 10), amps: Number(amps) || 0 }))
    .sort((a, b) => a.channel - b.channel);
};

// Bucket fleet history into time intervals; group by machine so each device
// becomes its own line.
const buildPerMachineSeries = (readings, intervalMs, fmtPattern) => {
  if (!readings || readings.length === 0) return { rows: [], machineIds: [] };
  const allMachineIds = new Set();
  const bucketMap = new Map();
  readings.forEach((r) => {
    const ts = r.timestamp;
    if (!ts) return;
    const mid = r.machineId || r.deviceId;
    if (!mid) return;
    allMachineIds.add(mid);
    const key = Math.floor(ts / intervalMs) * intervalMs;
    if (!bucketMap.has(key)) bucketMap.set(key, {});
    const bucket = bucketMap.get(key);
    if (!bucket[mid]) bucket[mid] = { sum: 0, count: 0 };
    bucket[mid].sum += Number(r.totalAmps) || 0;
    bucket[mid].count++;
  });

  const rows = [...bucketMap.entries()]
    .sort(([a], [b]) => a - b)
    .map(([ts, bucket]) => {
      const row = { timestamp: ts, time: fmt(ts, fmtPattern) };
      Object.entries(bucket).forEach(([mid, v]) => {
        row[mid] = parseFloat((v.sum / v.count).toFixed(3));
      });
      return row;
    });

  return { rows, machineIds: [...allMachineIds].sort() };
};

const COLORS_LINES = ['#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#3b82f6'];

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-800 border border-surface-600/50 rounded-lg p-3 shadow-modal">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-xs font-medium tabular-nums" style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' ? `${entry.value.toFixed(2)} A` : entry.value}
        </p>
      ))}
    </div>
  );
};

const EnergyDashboard = () => {
  const [machines, setMachines] = useState([]);
  const [machineData, setMachineData] = useState({});
  const [loading, setLoading] = useState(true);
  const subsRef = useRef([]);

  // Time-series controls + data
  const [dateRange, setDateRange] = useState({ from: Date.now() - 6 * 3600000, to: Date.now(), hours: 6, label: 'Last 6h' });
  const [aggLevel, setAggLevel] = useState('auto');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    dataService.getFleetReadingsHistory(dateRange.hours)
      .then((rows) => { if (!cancelled) setHistory(rows); })
      .catch((err) => { if (!cancelled) setHistoryError(err); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [dateRange]);

  useEffect(() => {
    const unsubAll = dataService.subscribeAllMachines((data, err) => {
      if (err || !data) { setLoading(false); return; }
      setMachines(data);
      const seeded = {};
      data.forEach((m) => { seeded[m.id] = m; });
      setMachineData((prev) => ({ ...seeded, ...prev }));

      data.forEach((m) => {
        if (subsRef.current.find((s) => s.id === m.id)) return;
        const unsub = dataService.subscribeMachineState(m.id, (docData) => {
          if (docData) setMachineData((prev) => ({ ...prev, [m.id]: docData }));
        });
        subsRef.current.push({ id: m.id, unsub });
      });
      setLoading(false);
    });
    return () => {
      try { unsubAll(); } catch (e) { /* noop */ }
      subsRef.current.forEach(({ unsub }) => { try { unsub(); } catch (e) { /* noop */ } });
      subsRef.current = [];
    };
  }, []);

  // Aggregate from live (non-stale) analog machines only
  const liveAnalog = machines
    .map((m) => machineData[m.id] || m)
    .filter((d) => d && d.monitorKind === 'analog' && !isStale(d.timestamp));

  const fleetTotalAmps = liveAnalog.reduce((s, d) => s + (Number(d.latest?.totalAmps) || 0), 0);

  const peakChannel = liveAnalog.reduce((best, d) => {
    const channels = readChannels(d.latest);
    if (channels.length === 0) return best;
    const top = channels.reduce((m, c) => (c.amps > m.amps ? c : m), { amps: 0 });
    return top.amps > best.amps ? { machineName: d.name || d.id, ...top } : best;
  }, { amps: 0, channel: null, machineName: null });

  const distribution = liveAnalog
    .filter((d) => (Number(d.latest?.totalAmps) || 0) > 0)
    .map((d) => ({ name: d.name || d.id, value: Number(d.latest.totalAmps) }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="section-title">Current Monitor</h1>
        <p className="section-subtitle">
          Live current draw from analog boards. Time-series history is not available — see note below.
        </p>
      </div>

      {/* KPI strip — all live, all from latest{} */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="kpi-card kpi-purple">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Fleet Current</p>
              <p className="metric-lg text-purple-400">{fleetTotalAmps.toFixed(2)}<span className="text-sm text-slate-500"> A</span></p>
            </div>
            <Zap className="w-5 h-5 text-purple-400 opacity-50" />
          </div>
          <p className="text-[10px] text-slate-500 mt-2">Sum across {liveAnalog.length} live board{liveAnalog.length === 1 ? '' : 's'}</p>
        </div>

        <div className="kpi-card kpi-cyan">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Peak Channel</p>
              <p className="metric-lg text-cyan-400">
                {peakChannel.amps > 0 ? `${peakChannel.amps.toFixed(2)} A` : '—'}
              </p>
            </div>
            <Activity className="w-5 h-5 text-cyan-400 opacity-50" />
          </div>
          <p className="text-[10px] text-slate-500 mt-2 truncate">
            {peakChannel.channel != null ? `CH${String(peakChannel.channel).padStart(2, '0')} on ${peakChannel.machineName}` : 'No active channels'}
          </p>
        </div>

        <div className="kpi-card kpi-blue">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Live Boards</p>
              <p className="metric-lg text-blue-400">{liveAnalog.length}<span className="text-lg text-slate-500">/{machines.length}</span></p>
            </div>
            <Activity className="w-5 h-5 text-blue-400 opacity-50" />
          </div>
          <p className="text-[10px] text-slate-500 mt-2">Reporting within 2 min</p>
        </div>

        <div className="kpi-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Offline</p>
              <p className="metric-lg text-slate-300">{machines.length - liveAnalog.length}</p>
            </div>
            <WifiOff className="w-5 h-5 text-slate-400 opacity-50" />
          </div>
          <p className="text-[10px] text-slate-500 mt-2">Stale or non-analog</p>
        </div>
      </div>

      {/* Distribution + per-device table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">Current Distribution</h3>
          {distribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={distribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value.toFixed(2)}A`}
                  dataKey="value"
                  stroke="none"
                >
                  {distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-slate-500 text-sm">No live current draw</div>
          )}
        </div>

        <div className="lg:col-span-2 card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-600/20">
            <h3 className="text-sm font-semibold text-slate-200">Per-device Current</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-surface-600/20">
                  {['Machine', 'Total (A)', 'Top Channel', 'Channels', 'Last Push', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {machines.map((m) => {
                  const d = machineData[m.id] || m;
                  const stale = isStale(d?.timestamp);
                  const isAnalog = d?.monitorKind === 'analog';
                  const channels = isAnalog ? readChannels(d.latest) : [];
                  const total = Number(d?.latest?.totalAmps ?? 0);
                  const top = channels.length > 0 ? [...channels].sort((a, b) => b.amps - a.amps)[0] : null;
                  const dash = <span className="text-slate-600">&mdash;</span>;
                  return (
                    <tr key={m.id} className={`border-b border-surface-600/10 hover:bg-surface-750/50 transition-colors ${stale ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 text-sm font-medium text-slate-200">{d?.name || m.id}</td>
                      <td className="px-4 py-3 text-sm font-mono tabular-nums text-slate-200">{stale || !isAnalog ? dash : total.toFixed(2)}</td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-400">
                        {stale || !top ? dash : `CH${String(top.channel).padStart(2, '0')} · ${top.amps.toFixed(2)}A`}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-400">{stale || !isAnalog ? dash : channels.length}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{d?.timestamp ? ageLabel(d.timestamp) : dash}</td>
                      <td className="px-4 py-3">
                        <span className={`badge ${stale ? 'bg-slate-500/15 text-slate-500 border-slate-500/20' : isAnalog && total >= 1.5 ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-slate-500/15 text-slate-400 border-slate-500/20'}`}>
                          {stale ? 'Offline' : isAnalog && total >= 1.5 ? 'Active' : isAnalog ? 'Idle' : 'No schema'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Current Draw chart — sourced from readings/ */}
      {(() => {
        const agg = getAggregation(dateRange.hours, aggLevel);
        const intervalMs = getAggregationMs(agg);
        const fmtPattern = intervalMs < 60000 ? 'HH:mm:ss' : dateRange.hours <= 48 ? 'HH:mm' : intervalMs >= 86400000 ? 'MM/dd' : 'MM/dd HH:mm';
        const { rows, machineIds } = buildPerMachineSeries(history, intervalMs, fmtPattern);
        const nameFor = (mid) => machines.find((m) => m.id === mid)?.name || mid;
        return (
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h3 className="text-sm font-semibold text-slate-200">Current Draw — per device</h3>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {historyLoading
                    ? 'Loading…'
                    : historyError
                      ? 'Failed to load readings'
                      : `${rows.length} bucket${rows.length === 1 ? '' : 's'} · ${machineIds.length} device${machineIds.length === 1 ? '' : 's'} · agg ${agg}`}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <DateRangePicker value={dateRange} onChange={setDateRange} />
              <AggregationSelector value={aggLevel} onChange={setAggLevel} />
            </div>
            {historyError ? (
              <div className="text-center py-10 text-red-400 text-sm">
                <p>Failed to load history.</p>
                <p className="text-[11px] text-red-400/70 mt-1">{historyError.message}</p>
                <p className="text-[10px] text-slate-500 mt-2">
                  Single-field index on{' '}
                  <code className="font-mono">readings.timestamp</code> required — Firestore should auto-create it.
                </p>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm">
                {historyLoading ? 'Loading…' : 'No readings in selected range'}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={rows}>
                  <defs>
                    {machineIds.map((mid, i) => (
                      <linearGradient key={mid} id={`grad_${mid}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS_LINES[i % COLORS_LINES.length]} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={COLORS_LINES[i % COLORS_LINES.length]} stopOpacity={0.02} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,42,69,0.5)" />
                  <XAxis dataKey="time" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={{ stroke: '#1e2a45' }} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10 }} axisLine={{ stroke: '#1e2a45' }} tickLine={false} label={{ value: 'Current (A)', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }} />
                  <Tooltip content={<DarkTooltip />} />
                  <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 11 }} />
                  {machineIds.map((mid, i) => (
                    <Area
                      key={mid}
                      type="monotone"
                      dataKey={mid}
                      stroke={COLORS_LINES[i % COLORS_LINES.length]}
                      fill={`url(#grad_${mid})`}
                      strokeWidth={2}
                      name={nameFor(mid)}
                      dot={false}
                      connectNulls
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        );
      })()}

      {/* Honest disclosure — current-only is by design, no kW/kWh */}
      <div className="card p-4 flex items-start gap-2.5 bg-surface-800/40">
        <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-slate-400">
          <span className="text-slate-300 font-medium">No kW / kWh / $.</span>{' '}
          The analog boards measure current only — no voltage, no power factor. Power and energy values would require multiplying by an assumed voltage, which is a fabrication. Current draw and per-channel anomaly detection are the honest signals this hardware can deliver.
        </p>
      </div>

      <p className="text-[10px] text-slate-600 text-right">Snapshot at {fmt(Date.now(), 'HH:mm:ss')}</p>
    </div>
  );
};

export default EnergyDashboard;
